import { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { apiClient } from '../../../../api/apiClient'

const STATUS_POLLING_INTERVAL = 5000

type TParsingJobState = 'PENDING' | 'IN_PROGRESS' | 'SUCCESS' | 'FAILED' | 'ERROR' | string

type TParsingJobStatus = {
  state: TParsingJobState
  progress: number
  errorMessage: string | null
}

type TPresignedFile = {
  key: string
  url: string
}

type TPresignedFilesResponse = Record<string, TPresignedFile>

type TUploadS3FilesResponse = {
  jobId: string
  message?: string
}

const getSignedHeaders = (presignedUrl: string) => {
  try {
    const url = new URL(presignedUrl)
    const signedHeaders = url.searchParams.get('X-Amz-SignedHeaders')
    return signedHeaders ? signedHeaders.split(';') : []
  } catch {
    return []
  }
}

const uploadFileToS3 = async (
  file: File,
  presignedUrl: string,
  signal?: AbortSignal
) => {
  const signedHeaders = getSignedHeaders(presignedUrl)
  const headers: HeadersInit = {}

  if (signedHeaders.includes('content-type')) {
    headers['Content-Type'] = file.type || 'text/csv'
  }

  const response = await fetch(presignedUrl, {
    method: 'PUT',
    body: file,
    headers,
    signal
  })

  if (!response.ok) {
    throw new Error(`Failed to upload ${file.name}`)
  }
}

export const useFilesImport = () => {
  const [isFilesUploading, setIsFilesUploading] = useState(false)
  const [filesUploadProgress, setFilesUploadProgress] = useState(0)
  const controllerRef = useRef<AbortController | null>(null)
  const isMountedRef = useRef(true)
  useEffect(() => () => {
    isMountedRef.current = false
    controllerRef.current?.abort()
  }, [])

  const fetchParsingStatus = async (jobId: string): Promise<TParsingJobStatus> =>
    await apiClient
      .get(`/parse/status/${jobId}`)
      .then((response) => response?.data)

  const wait = (timeout = STATUS_POLLING_INTERVAL) =>
    new Promise((resolve) => {
      setTimeout(resolve, timeout)
    })

  const waitForJobCompletion = async (jobId: string) => {
    while (isMountedRef.current) {
      const status = await fetchParsingStatus(jobId)
      if (!isMountedRef.current) return

      setFilesUploadProgress(
        Math.min(100, Math.max(0, status?.progress ?? 0))
      )

      if (status?.state === 'SUCCESS') {
        setFilesUploadProgress(100)
        return
      }

      if (status?.state === 'FAILED' || status?.state === 'ERROR') {
        throw new Error(status?.errorMessage ?? 'Import failed')
      }

      await wait()
    }
  }

  const importFiles = async (
    files: File[],
    onSuccess?: () => void
  ) => {
    try {
      setIsFilesUploading(true)
      setFilesUploadProgress(0)

      controllerRef.current = new AbortController()
      const { signal } = controllerRef.current
      const presignedFiles = await apiClient.post(
        '/files/uploadFiles/presign',
        undefined,
        { signal }
      ).then((response) => response?.data as TPresignedFilesResponse)

      if (!presignedFiles) {
        throw new Error('Import failed: missing presigned urls')
      }

      const filesWithPresignedData = files.map((file) => {
        const presignedData = presignedFiles[file.name]

        if (!presignedData?.key || !presignedData?.url) {
          throw new Error(`Import failed: missing upload info for ${file.name}`)
        }

        return {
          file,
          key: presignedData.key,
          url: presignedData.url
        }
      })

      await Promise.all(
        filesWithPresignedData.map(({ file, url }) =>
          uploadFileToS3(file, url, signal))
      )

      const keys = filesWithPresignedData.map(({ key }) => key)
      const { jobId } = await apiClient.post(
        '/files/uploadS3Files',
        keys,
        { signal }
      ).then((response) => response?.data as TUploadS3FilesResponse)

      if (!jobId) {
        throw new Error('Import failed: empty job id')
      }

      await waitForJobCompletion(jobId)

      onSuccess?.()
    } catch (e: any) {
      toast.error(
        e?.message ?? 'Something went wrong',
        {
          position: 'top-center',
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          rtl: false,
          pauseOnFocusLoss: false,
          draggable: false,
          pauseOnHover: false,
          style: {
            background: 'white',
            color: 'red',
            border: 'none'
          }
        }
      )
    } finally {
      controllerRef.current = null
      if (isMountedRef.current) {
        setIsFilesUploading(false)
      }
    }
  }

  return {
    importFiles,
    isFilesUploading,
    filesUploadProgress
  }
}
